/**
 * Session tracking helper for Express
 * Provides graceful degradation of session tracking for non-Next.js environments
 */

import type { Request, Response } from 'express';
import type { DetectionResult } from '@kya-os/agentshield';

export interface SessionData {
  id: string;
  agent: string;
  confidence: number;
  detectedAt: number;
  expires: number;
}

/**
 * Express-compatible session tracker
 * Uses cookies when available, falls back to headers
 */
export class ExpressSessionTracker {
  private readonly cookieName = '__agentshield_session';

  /**
   * Check for existing session from Express request
   */
  check(req: Request): SessionData | null {
    try {
      // Try cookie-based session first
      if (req.cookies && req.cookies[this.cookieName]) {
        try {
          const decoded = Buffer.from(
            req.cookies[this.cookieName],
            'base64'
          ).toString();
          const session: SessionData = JSON.parse(decoded);

          // Check expiration
          if (session.expires > Date.now()) {
            return session;
          }
        } catch {
          // Invalid session data
        }
      }

      // Try header-based session (for clients that don't support cookies)
      const sessionHeader = req.get('X-AgentShield-Session');
      if (sessionHeader) {
        try {
          const session: SessionData = JSON.parse(
            Buffer.from(sessionHeader, 'base64').toString()
          );
          if (session.expires > Date.now()) {
            return session;
          }
        } catch {
          // Invalid session data
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Track a new session in Express response
   */
  track(res: Response, result: DetectionResult): void {
    try {
      if (!result.isAgent) return;

      const session: SessionData = {
        id: this.generateId(),
        agent: result.detectedAgent?.name || 'unknown',
        confidence: result.confidence,
        detectedAt: Date.now(),
        expires: Date.now() + 3600000, // 1 hour
      };

      const encoded = Buffer.from(JSON.stringify(session)).toString('base64');

      // Set cookie if cookie-parser is available
      if (res.cookie) {
        res.cookie(this.cookieName, encoded, {
          httpOnly: true,
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'lax',
          maxAge: 3600000, // 1 hour in milliseconds
        });
      }

      // Always set header as fallback
      res.setHeader('X-AgentShield-Session', encoded);
      res.setHeader('X-AgentShield-Session-Agent', session.agent);
      res.setHeader('X-AgentShield-Session-Id', session.id);
    } catch (error) {
      // Fail gracefully
      if (process.env.DEBUG_AGENTSHIELD) {
        console.warn('AgentShield: Failed to track session:', error);
      }
    }
  }

  /**
   * Generate a simple ID without crypto dependency
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Helper to add session tracking to Express middleware
 */
export function withSessionTracking(
  middleware: Function,
  config?: { enabled?: boolean }
): Function {
  if (!config?.enabled) {
    return middleware;
  }

  const tracker = new ExpressSessionTracker();

  return async function (req: Request, res: Response, next: Function) {
    // Check for existing session
    const session = tracker.check(req);

    if (session) {
      // Attach session to request
      (req as any).agentShield = {
        ...(req as any).agentShield,
        session,
        result: {
          isAgent: true,
          confidence: session.confidence,
          detectedAgent: { name: session.agent },
          timestamp: new Date(),
          verificationMethod: 'session',
        },
      };

      // Set response headers
      res.setHeader('X-AgentShield-Detected', 'true');
      res.setHeader('X-AgentShield-Agent', session.agent);
      res.setHeader('X-AgentShield-Session', 'continued');
    }

    // Call original middleware
    const originalEnd = res.end;
    res.end = function (...args: any[]) {
      // Track new sessions before sending response
      const agentShield = (req as any).agentShield;
      if (agentShield?.result?.isAgent && !agentShield.session) {
        tracker.track(res, agentShield.result);
      }

      return originalEnd.apply(res, args as any);
    };

    middleware(req, res, next);
  };
}
